import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ShieldAlert } from 'lucide-react';

interface AuthCallbackProps {
  onLogin: (token: string, user: unknown) => void;
}

function AuthCallback({ onLogin }: AuthCallbackProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');

  useEffect(() => {
    const completeSsoLogin = async () => {
      const code = searchParams.get('code');
      if (!code) {
        setError('Missing SSO session code.');
        return;
      }

      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/api/auth/sso/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          onLogin(data.data.token, data.data.user);
          navigate('/dashboard', { replace: true });
          return;
        }

        setError(data.error || 'Unable to complete SSO login.');
      } catch {
        setError('Unable to reach the authentication service.');
      }
    };

    completeSsoLogin();
  }, [navigate, onLogin, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <Card className="w-full max-w-md border-0 shadow-surface-3">
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            {error ? <ShieldAlert className="h-6 w-6" /> : <Loader2 className="h-6 w-6 animate-spin" />}
          </div>
          <CardTitle className="text-2xl font-display font-bold">
            {error ? 'SSO sign-in failed' : 'Completing sign-in'}
          </CardTitle>
          <CardDescription>
            {error || 'Verifying your AuthorizeMe session...'}
          </CardDescription>
        </CardHeader>
        {error && (
          <CardContent>
            <Button asChild className="w-full h-11 rounded-xl font-semibold">
              <Link to="/login">Return to login</Link>
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

export default AuthCallback;
